import * as THREE from 'three';

/**
 * RunnerZombieModel - A fast, skinny zombie with red veins
 * Low-poly but visually distinct from regular zombies.
 * Size is between regular zombies and boss zombies.
 */
export class RunnerZombieModel extends THREE.Group {
    constructor(options = {}) {
        super();
        
        // Configuration options with defaults
        this.config = {
            bodyColor: options.bodyColor || 0x2a2a2a, // Darker gray/pale body
            veinColor: options.veinColor || 0xff0000, // Bright red veins
            veinEmissive: options.veinEmissive || 0x330000, // Subtle red glow for veins
            eyeColor: options.eyeColor || 0xff2200, // Reddish eyes
            eyeEmissive: options.eyeEmissive || 0xff2200, // Reddish glow
            eyeIntensity: options.eyeIntensity || 0.9, // Bright eyes
            scale: options.scale || 0.65, // Size between regular (0.56) and boss (0.8)
            animationSpeed: options.animationSpeed || 1.3 // Faster animation for runner
        };
        
        // Animation state
        this.animationState = {
            running: true,
            attacking: false,
            attackCooldown: 0,
            leapAttack: false,
            leapAttackCooldown: 0,
            time: 0
        };
        
        // Create body parts
        this.createBodyParts();
        
        // Set up structure
        this.assembleModel();
        
        // Apply initial scale
        this.scale.set(this.config.scale, this.config.scale, this.config.scale);
    }
    
    /**
     * Create all individual body part meshes
     */
    createBodyParts() {
        // Create base materials
        this.bodyMaterial = new THREE.MeshStandardMaterial({ 
            color: this.config.bodyColor,
            roughness: 0.9,
            metalness: 0.1,
        });
        
        // Create vein material
        this.veinMaterial = new THREE.MeshStandardMaterial({
            color: this.config.veinColor,
            emissive: this.config.veinEmissive,
            emissiveIntensity: 0.9, // Increased from 0.6 for stronger glow
            roughness: 0.5, // Reduced for more shine
            metalness: 0.3  // Increased slightly
        });
        
        // Create glowing eye material
        this.eyeMaterial = new THREE.MeshStandardMaterial({ 
            color: this.config.eyeColor,
            emissive: this.config.eyeEmissive,
            emissiveIntensity: this.config.eyeIntensity,
            roughness: 0.2,
            metalness: 0.7
        });
        
        // Head with eyes
        this.head = new THREE.Mesh(
            new THREE.BoxGeometry(0.5, 0.6, 0.55), // Slightly narrower head
            this.bodyMaterial
        );
        
        // Eyes (as separate meshes for glow effect)
        this.leftEye = new THREE.Mesh(
            new THREE.SphereGeometry(0.08, 6, 6),
            this.eyeMaterial
        );
        
        this.rightEye = new THREE.Mesh(
            new THREE.SphereGeometry(0.08, 6, 6),
            this.eyeMaterial
        );
        
        // Torso - much skinnier than other zombies
        this.torso = new THREE.Mesh(
            new THREE.BoxGeometry(0.7, 1.5, 0.35), // Taller, narrower torso
            this.bodyMaterial
        );
        
        // Arms - thin and wiry
        this.upperRightArm = new THREE.Mesh(
            new THREE.BoxGeometry(0.2, 0.75, 0.2), // Thin arms
            this.bodyMaterial
        );
        
        this.lowerRightArm = new THREE.Mesh(
            new THREE.BoxGeometry(0.18, 0.7, 0.18),
            this.bodyMaterial
        );
        
        this.upperLeftArm = new THREE.Mesh(
            new THREE.BoxGeometry(0.2, 0.75, 0.2),
            this.bodyMaterial
        );
        
        this.lowerLeftArm = new THREE.Mesh(
            new THREE.BoxGeometry(0.18, 0.7, 0.18),
            this.bodyMaterial
        );
        
        // Legs - long and thin
        this.upperRightLeg = new THREE.Mesh(
            new THREE.BoxGeometry(0.2, 0.85, 0.2), // Longer, thinner legs
            this.bodyMaterial
        );
        
        this.lowerRightLeg = new THREE.Mesh(
            new THREE.BoxGeometry(0.18, 0.75, 0.18),
            this.bodyMaterial
        );
        
        this.upperLeftLeg = new THREE.Mesh(
            new THREE.BoxGeometry(0.2, 0.85, 0.2),
            this.bodyMaterial
        );
        
        this.lowerLeftLeg = new THREE.Mesh(
            new THREE.BoxGeometry(0.18, 0.75, 0.18),
            this.bodyMaterial
        );
        
        // Create red veins as a collection of thin lines
        this.veins = [];
        
        // Create veins for different body parts
        this.createVeinsForPart(this.head, 6); // Increased from 5
        this.createVeinsForPart(this.torso, 15); // Increased from 12
        this.createVeinsForPart(this.upperRightArm, 5); // Increased from 4
        this.createVeinsForPart(this.upperLeftArm, 5); // Increased from 4
        this.createVeinsForPart(this.lowerRightArm, 4); // Increased from 3
        this.createVeinsForPart(this.lowerLeftArm, 4); // Increased from 3
        this.createVeinsForPart(this.upperRightLeg, 5); // Increased from 4
        this.createVeinsForPart(this.upperLeftLeg, 5); // Increased from 4
        this.createVeinsForPart(this.lowerRightLeg, 4); // Increased from 3
        this.createVeinsForPart(this.lowerLeftLeg, 4); // Increased from 3
    }
    
    /**
     * Creates vein geometry for a specific body part
     * @param {THREE.Mesh} part - The body part to add veins to
     * @param {number} count - How many veins to create
     */
    createVeinsForPart(part, count) {
        const partSize = new THREE.Vector3();
        part.geometry.computeBoundingBox();
        part.geometry.boundingBox.getSize(partSize);
        
        for (let i = 0; i < count; i++) {
            // Create a vein as a thin, slightly curved cylinder
            const veinLength = 0.15 + Math.random() * 0.2; // Increased from 0.1 + random * 0.15
            const veinRadius = 0.02 + Math.random() * 0.015; // Increased from 0.01 + random * 0.01
            
            // Use a cylinder for the vein
            const vein = new THREE.Mesh(
                new THREE.CylinderGeometry(veinRadius, veinRadius, veinLength, 4), // Increased from 3 segments for better appearance
                this.veinMaterial
            );
            
            // Position the vein on the surface of the part
            vein.position.set(
                (Math.random() - 0.5) * partSize.x * 0.9, // Increased spread from 0.8
                (Math.random() - 0.5) * partSize.y * 0.9, // Increased spread from 0.8
                (Math.random() * 0.5 + 0.5) * partSize.z * 0.6 // Increased from 0.5 to make more prominent
            );
            
            // Random rotation for organic look
            vein.rotation.set(
                Math.random() * Math.PI,
                Math.random() * Math.PI,
                Math.random() * Math.PI
            );
            
            part.add(vein);
            this.veins.push(vein);
        }
    }
    
    /**
     * Assemble the model with proper hierarchy
     */
    assembleModel() {
        // Create joints (empty groups for rotation)
        this.neck = new THREE.Group();
        this.rightShoulder = new THREE.Group();
        this.leftShoulder = new THREE.Group();
        this.rightElbow = new THREE.Group();
        this.leftElbow = new THREE.Group();
        this.rightHip = new THREE.Group();
        this.leftHip = new THREE.Group();
        this.rightKnee = new THREE.Group();
        this.leftKnee = new THREE.Group();
        
        // Add parts to the model with proper hierarchy
        
        // Head and neck
        this.neck.position.set(0, 0.8, 0);
        this.neck.add(this.head);
        this.head.position.set(0, 0.3, 0);
        
        // Add eyes to head
        this.leftEye.position.set(0.15, 0.05, 0.28);
        this.rightEye.position.set(-0.15, 0.05, 0.28);
        this.head.add(this.leftEye);
        this.head.add(this.rightEye);
        
        // Add neck to torso
        this.torso.add(this.neck);
        
        // Right arm
        this.rightShoulder.position.set(-0.45, 0.7, 0);
        this.rightShoulder.add(this.upperRightArm);
        this.upperRightArm.position.set(0, -0.35, 0);
        this.torso.add(this.rightShoulder);
        
        this.rightElbow.position.set(0, -0.75, 0);
        this.rightElbow.add(this.lowerRightArm);
        this.lowerRightArm.position.set(0, -0.35, 0);
        this.upperRightArm.add(this.rightElbow);
        
        // Left arm
        this.leftShoulder.position.set(0.45, 0.7, 0);
        this.leftShoulder.add(this.upperLeftArm);
        this.upperLeftArm.position.set(0, -0.35, 0);
        this.torso.add(this.leftShoulder);
        
        this.leftElbow.position.set(0, -0.75, 0);
        this.leftElbow.add(this.lowerLeftArm);
        this.lowerLeftArm.position.set(0, -0.35, 0);
        this.upperLeftArm.add(this.leftElbow);
        
        // Right leg
        this.rightHip.position.set(-0.25, -0.75, 0);
        this.rightHip.add(this.upperRightLeg);
        this.upperRightLeg.position.set(0, -0.45, 0);
        this.torso.add(this.rightHip);
        
        this.rightKnee.position.set(0, -0.85, 0);
        this.rightKnee.add(this.lowerRightLeg);
        this.lowerRightLeg.position.set(0, -0.4, 0);
        this.upperRightLeg.add(this.rightKnee);
        
        // Left leg
        this.leftHip.position.set(0.25, -0.75, 0);
        this.leftHip.add(this.upperLeftLeg);
        this.upperLeftLeg.position.set(0, -0.45, 0);
        this.torso.add(this.leftHip);
        
        this.leftKnee.position.set(0, -0.85, 0);
        this.leftKnee.add(this.lowerLeftLeg);
        this.lowerLeftLeg.position.set(0, -0.4, 0);
        this.upperLeftLeg.add(this.leftKnee);
        
        // Add torso to the root of the model
        this.add(this.torso);
        
        // Center the model so the feet are at y=0
        this.torso.position.set(0, 2.2, 0); // Slightly taller to look more lanky
        
        // Set initial runner zombie pose - aggressive running stance
        this.setRunnerPose();
    }
    
    /**
     * Set the default runner zombie pose - aggressive, ready to sprint
     */
    setRunnerPose() {
        // Forward leaning posture
        this.torso.rotation.x = 0.25; // Lean forward
        this.neck.rotation.x = -0.25; // Head looking forward
        
        // Arms bent and ready
        this.rightShoulder.rotation.z = -0.2;
        this.leftShoulder.rotation.z = 0.2;
        this.rightShoulder.rotation.x = -0.5; // Arms forward
        this.leftShoulder.rotation.x = -0.5;
        
        // Bent elbows
        this.rightElbow.rotation.x = -0.8;
        this.leftElbow.rotation.x = -0.8;
        
        // Legs ready to run
        this.rightHip.rotation.x = -0.2;
        this.leftHip.rotation.x = -0.2;
        this.rightKnee.rotation.x = 0.4; // Slight bend
        this.leftKnee.rotation.x = 0.4;
    }
    
    /**
     * Update animations based on elapsed time
     * @param {number} deltaTime - Time since last frame in seconds
     */
    update(deltaTime) {
        // Accumulate animation time
        this.animationState.time += deltaTime * this.config.animationSpeed;
        
        // Handle attack cooldowns
        if (this.animationState.attackCooldown > 0) {
            this.animationState.attackCooldown -= deltaTime;
            if (this.animationState.attackCooldown <= 0) {
                this.animationState.attacking = false;
            }
        }
        
        if (this.animationState.leapAttackCooldown > 0) {
            this.animationState.leapAttackCooldown -= deltaTime;
            if (this.animationState.leapAttackCooldown <= 0) {
                this.animationState.leapAttack = false;
            }
        }
        
        // Animate based on current state
        if (this.animationState.leapAttack) {
            this.animateLeapAttack(this.animationState.time);
        } else if (this.animationState.attacking) {
            this.animateAttack(this.animationState.time);
        } else {
            this.animateRun(this.animationState.time);
        }
        
        // Animate veins pulsating
        this.animateVeins(this.animationState.time);
    }
    
    /**
     * Animate the pulsating of veins
     */
    animateVeins(time) {
        this.veins.forEach((vein, i) => {
            // Pulse the emissive intensity for a more dramatic glowing effect
            if (vein.material) {
                vein.material.emissiveIntensity = 0.5 + 0.5 * Math.sin(time * 3 + i * 0.5); // Increased from 0.3 + 0.3
            }
        });
    }
    
    /**
     * Animate the runner zombie's fast run
     */
    animateRun(time) {
        // Fast, aggressive running cycle
        const armSwing = Math.sin(time * 4) * 0.4; // Faster, wider swing
        const legSwing = Math.sin(time * 4) * 0.7; // Exaggerated leg movement
        const bodyBob = Math.sin(time * 8) * 0.05; // Quick up/down motion
        
        // Body movement
        this.torso.position.y = 2.2 + bodyBob; // Vertical bouncing
        this.torso.rotation.x = 0.25 + Math.sin(time * 4) * 0.05; // Forward lean with slight variation
        
        // Arm swinging - opposite to legs for natural running motion
        this.rightShoulder.rotation.x = -0.5 - armSwing;
        this.leftShoulder.rotation.x = -0.5 + armSwing;
        
        // Leg movement - exaggerated for running
        this.rightHip.rotation.x = -0.2 + legSwing;
        this.leftHip.rotation.x = -0.2 - legSwing;
        
        // Dynamic knee bend based on leg position
        const rightKneeBend = Math.max(0.4, 0.8 - legSwing * 2); // More bend when leg is back
        const leftKneeBend = Math.max(0.4, 0.8 + legSwing * 2);
        this.rightKnee.rotation.x = rightKneeBend;
        this.leftKnee.rotation.x = leftKneeBend;
        
        // Head looking for prey
        this.neck.rotation.y = Math.sin(time * 2) * 0.2;
    }
    
    /**
     * Trigger the basic attack animation
     */
    startAttack() {
        if (!this.animationState.attacking && !this.animationState.leapAttack) {
            this.animationState.attacking = true;
            this.animationState.attackCooldown = 0.8; // Fast attack animation
            
            // Reset positions for attack animation
            this.rightShoulder.rotation.x = -0.7;
            this.leftShoulder.rotation.x = -0.7;
        }
    }
    
    /**
     * Trigger leap attack animation
     */
    startLeapAttack() {
        if (!this.animationState.attacking && !this.animationState.leapAttack) {
            this.animationState.leapAttack = true;
            this.animationState.leapAttackCooldown = 1.5; // Full leap cycle
            
            // Reset positions for leap
            this.torso.rotation.x = 0.3;
            this.rightHip.rotation.x = -0.6;
            this.leftHip.rotation.x = -0.6;
        }
    }
    
    /**
     * Animate the runner zombie's quick slashing attack
     */
    animateAttack(time) {
        // Calculate attack phase (0 to 1)
        const phase = 1 - (this.animationState.attackCooldown / 0.8);
        
        // Fast, slashing attack motion
        if (phase < 0.3) {
            // Wind up phase - very quick
            const windUpPhase = phase / 0.3;
            this.rightShoulder.rotation.x = -0.7 - windUpPhase * 0.8;
            this.leftShoulder.rotation.x = -0.7;
            this.rightElbow.rotation.x = -0.8 - windUpPhase * 0.7;
            
            // Twist body slightly for more force
            this.torso.rotation.y = -windUpPhase * 0.2;
            this.torso.rotation.z = -windUpPhase * 0.1;
        } else {
            // Strike phase - extremely fast slash
            const strikePhase = (phase - 0.3) / 0.7;
            
            // Right arm slashes across body
            this.rightShoulder.rotation.x = -1.5 + strikePhase * 2.0;
            this.rightShoulder.rotation.y = strikePhase * 0.5;
            this.rightElbow.rotation.x = -1.5 + strikePhase * 1.0;
            
            // Twist body with the strike
            this.torso.rotation.y = -0.2 + strikePhase * 0.4;
            this.torso.rotation.z = -0.1 + strikePhase * 0.2;
            
            // Head follows the slash
            this.neck.rotation.y = -0.1 + strikePhase * 0.4;
        }
    }
    
    /**
     * Animate the runner zombie's special leap attack
     */
    animateLeapAttack(time) {
        // Calculate leap phase (0 to 1)
        const phase = 1 - (this.animationState.leapAttackCooldown / 1.5);
        
        if (phase < 0.2) {
            // Phase 1: Crouch to prepare for jump
            const p = phase / 0.2;
            
            // Lower body position
            this.position.y = -p * 0.3;
            
            // Crouch posture
            this.torso.rotation.x = 0.3 - p * 0.1;
            this.rightKnee.rotation.x = 0.4 + p * 1.1;
            this.leftKnee.rotation.x = 0.4 + p * 1.1;
            
            // Arms pull back
            this.rightShoulder.rotation.x = -0.5 - p * 1.0;
            this.leftShoulder.rotation.x = -0.5 - p * 1.0;
        }
        else if (phase < 0.4) {
            // Phase 2: Launch into the air
            const p = (phase - 0.2) / 0.2;
            
            // Jump up
            this.position.y = -0.3 + p * 1.1; // Higher jump than boss
            
            // Extend legs
            this.rightKnee.rotation.x = 1.5 - p * 1.5;
            this.leftKnee.rotation.x = 1.5 - p * 1.5;
            
            // Extend arms forward
            this.rightShoulder.rotation.x = -1.5 + p * 1.0;
            this.leftShoulder.rotation.x = -1.5 + p * 1.0;
            
            // Lean forward
            this.torso.rotation.x = 0.2 + p * 0.4;
        }
        else if (phase < 0.7) {
            // Phase 3: In the air, pounce position
            const p = (phase - 0.4) / 0.3;
            
            // Maintain height at peak of jump
            this.position.y = 0.8;
            
            // Full pounce pose with arms and legs extended
            this.rightShoulder.rotation.x = -0.5;
            this.leftShoulder.rotation.x = -0.5;
            this.rightElbow.rotation.x = -1.2;
            this.leftElbow.rotation.x = -1.2;
            
            // Lean forward more as preparing to land
            this.torso.rotation.x = 0.6 + p * 0.3;
        }
        else {
            // Phase 4: Landing and striking
            const p = (phase - 0.7) / 0.3;
            
            // Come back down
            this.position.y = 0.8 * (1 - p);
            
            // Land with arms slashing down
            this.rightShoulder.rotation.x = -0.5 + p * 1.0;
            this.leftShoulder.rotation.x = -0.5 + p * 1.0;
            
            // Impact crouch
            this.rightKnee.rotation.x = p * 1.0;
            this.leftKnee.rotation.x = p * 1.0;
            
            // Return to forward lean
            this.torso.rotation.x = 0.9 - p * 0.65;
        }
    }
    
    /**
     * Helper method to make all meshes cast and receive shadows
     */
    enableShadows() {
        this.traverse(child => {
            if (child instanceof THREE.Mesh) {
                child.castShadow = true;
                child.receiveShadow = true;
            }
        });
    }
} 